import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useState, useEffect, useRef } from 'react';
import { elapsedFraction } from '../../lib/campaign-clock';
import { stepPanel } from './rotation';
import { LiveFeedPanel } from './LiveFeedPanel';
import { Carousel } from '../../components/Carousel';
import { BossCard } from '../boss/BossCard';
import { spriteFor } from '../boss/sprites';

// Public board (M5): full-screen wall-monitor view. Rotates through
// panels every N seconds (from config). Anonymised by default — handle
// only; display_name only for users who opted in. Read-only, no chrome.
// See ADR-0002 + spec §4.4, §8.

interface BoardUser { handle: string; name: string | null; }
interface PanelData {
  on_fire?: { handle: string; name: string | null; streak: number }[];
  just_shipped?: { type: string; when: string; target: string; who: BoardUser; title: string }[];
  kudos_stream?: { when: string; to: BoardUser; from: string; note: string | null }[];
  // M5.15: multi-competency rewards. The featured quest now carries
  // `splits` (array of {competency_id, xp, coins?}) instead of
  // the old single {xp, competency_id}. The panel renders total
  // XP + total coins + a per-split breakdown; the old fields are
  // gone.
  quest_of_the_day?: { id: string; title: string; description: string; splits: Array<{ competency_id: string; xp: number; coins?: number }> } | null;
  class_spotlight?: Record<string, number>;
  todays_standouts?: { handle: string; name: string | null; xp: number }[];
  competency_heatmap?: { competencies: { id: string; name: string; dimension: string; sort_order: number }[]; avgs: { competency_id: string; avg: number }[] };
  // M5.11 live_feed: served by a separate endpoint
  // (/api/public-board/live-feed) polled at 2s by the
  // web client. The parent /api/public-board does not
  // populate this key.
}
interface BoardConfig { panels: string[]; rotation_seconds: number; anonymise: boolean; sound: boolean; }
interface BoardResponse { team_id: string; team_name?: string | null; config: BoardConfig; panels_data: PanelData; adventure?: {
  active: {
    id: string; name: string; place?: string | null; starts_at: string; ends_at: string;
    committed_count: number; completed_count: number; predictability_pct: number; side_quest_count?: number;
    goals: Array<{ id: string; name: string; kind: 'tickets' | 'count'; hit_when?: string; target: number; progress?: number; hit_now?: boolean; users?: Array<{ user_id: string; handle: string; name: string | null; color?: string | null }> }>;
  } | null;
  recent_closed: { name: string; ends_at: string; closed_summary: {
    predictability_pct: number; committed_count: number; completed_count: number; tier: string | null;
  } } | null;
  bosses: Array<{ id: string; name: string; hp: number | null; damage_dealt: number; ends_at: string; status: 'draft' | 'active' | 'vanquished' | 'survived' | 'archived'; tickets_total?: number; tickets_closed?: number; users?: Array<{ user_id: string; handle: string; name: string | null; color?: string | null; damage?: number }> }>;
}; }

const CLASS_ICONS: Record<string, string> = { crafter: '🔨', guardian: '🛡️', explorer: '🧭', diplomat: '🤝', none: '·' };

export function PublicBoardPage() {
  const { data } = useQuery<BoardResponse>({
    queryKey: ['public-board'],
    queryFn: () => api<BoardResponse>('/api/public-board'),
    refetchInterval: 5_000, // < 5s live-update exit criteria
  });
  // Resolve the current team's display name from the team_id
  // returned by the board. Helps operators confirm the wall TV is
  // showing the right team — the default team is whichever one
  // the admin most recently configured (see public-board.routes.ts).
  const teamName = useTeamName(data?.team_id, data?.team_name);
  const config = data?.config;
  const panels = config?.panels ?? [];
  // v0.8.9: when an adventure is active the operator wants the
  // configured panels to actually rotate, not the board to be
  // pinned to the three-panel AdventureBoard layout (which used
  // to suppress rotation entirely). The adventure becomes the
  // first item in the rotation; everything else comes after.
  const hasActiveAdventure = Boolean(data?.adventure?.active);
  const rotationPanels = hasActiveAdventure ? ['adventure', ...panels] : panels;
  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);
  idxRef.current = idx;

  // A manual advance RESTARTS the rotation clock.
  //
  // Without this, clicking to the panel you want gives you whatever was
  // left of the current interval — possibly a fraction of a second — and
  // the board moves on before you have read it. The whole point of the
  // click is "show me this one", so the chosen panel gets a full turn.
  //
  // Bumped by every manual move, and in the effect's dependencies, so the
  // interval is torn down and rebuilt from now.
  const [rotationEpoch, setRotationEpoch] = useState(0);

  /** Move `delta` panels, wrapping, and restart the clock. */
  const advance = (delta: number) => {
    if (rotationPanels.length === 0) return;
    setIdx((i) => stepPanel(i, delta, rotationPanels.length));
    setRotationEpoch((e) => e + 1);
  };

  /** Jump straight to a panel. Also restarts the clock: the dot you clicked
   *  is the one you want to look at. */
  const goTo = (i: number) => {
    setIdx(i);
    setRotationEpoch((e) => e + 1);
  };

  useEffect(() => {
    if (rotationPanels.length === 0) return;
    const interval = setInterval(() => {
      setIdx((i) => (i + 1) % rotationPanels.length);
    }, (config?.rotation_seconds ?? 5) * 1000);
    return () => clearInterval(interval);
  }, [rotationPanels.length, config?.rotation_seconds, rotationEpoch]);

  // Reset index if out of range after config change.
  useEffect(() => { if (idx >= rotationPanels.length) setIdx(0); }, [idx, rotationPanels.length]);

  // Keyboard: on a PC, arrows are the obvious way to step a slideshow and
  // they cannot collide with anything in a panel. A wall display has no
  // keyboard, so this changes nothing for the TV.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') { advance(1); e.preventDefault(); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { advance(-1); e.preventDefault(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotationPanels.length]);

  // Click anywhere to advance, with two guards that matter on a page that
  // is mostly read-only but does contain a scrollable feed.
  //
  //   * a DRAG is not a click. Selecting text to copy it should not skip
  //     the panel.
  //   * a click that lands on something actually interactive belongs to
  //     that thing, not to the rotation.
  const downAt = useRef<{ x: number; y: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => { downAt.current = { x: e.clientX, y: e.clientY }; };
  const onClick = (e: React.MouseEvent) => {
    const d = downAt.current;
    if (d && (Math.abs(e.clientX - d.x) > 6 || Math.abs(e.clientY - d.y) > 6)) return;
    const el = e.target as HTMLElement;
    if (el.closest('a, button, input, select, textarea, [role="button"]')) return;
    advance(e.shiftKey || e.altKey || e.metaKey ? -1 : 1);
  };

  const reduceMotion = usePrefersReducedMotion();
  const currentPanel = rotationPanels[idx];

  return (
    <div className="fixed inset-0 flex flex-col text-ink" style={{ fontSize: 'clamp(0.875rem, 0.8vw, 1.4rem)' }}>
      <header className="flex items-center justify-between px-8 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-md text-bg text-xl font-bold" style={{ backgroundImage: 'linear-gradient(135deg, var(--fantasy-gold) 0%, #2a1f0a 100%)' }}>M</span>
          <span className="text-2xl font-semibold">Mirabili</span>
          {teamName ? (
            <span
              className="ml-2 rounded bg-bg-elevated px-2 py-0.5 text-base font-medium text-ink-muted"
              title="The board is scoped to a single team. Add ?team_id=<id> to the URL to change teams."
            >
              {teamName}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-4 text-lg text-ink-muted">
          {config?.anonymise ? <span className="rounded bg-bg-elevated px-2 py-0.5 text-sm">anonymised</span> : null}
          <Clock />
        </div>
      </header>

      <main
        className="flex-1 overflow-hidden cursor-pointer"
        onPointerDown={onPointerDown}
        onClick={onClick}
        title="Click to advance · shift-click for the previous panel · ← → also work"
      >
        {data && currentPanel ? (
          // Phase 5: when an adventure is active the operator now
          // wants the configured panels to rotate, with the
          // adventure three-panel layout itself becoming a slot
          // in the rotation (handled by the 'adventure' case in
          // the Panel switch below).
          //
          // v0.8.9: h-full on the wrapper so the panel's h-full
          // resolves against a definite height. Without it the
          // wrapper is auto-height, PanelShell's h-full falls back
          // to auto, and the live feed's own scroll can't compute
          // a bounded height (it was clipped by <main>'s
          // overflow-hidden with no way to reach the extra rows).
          <div key={currentPanel + '-' + idx} className={`h-full ${reduceMotion ? '' : 'mirabili-panel-fade'}`}>
            <Panel name={currentPanel} data={data.panels_data} adventure={data.adventure ?? null} />
          </div>
        ) : (
          <div className="grid h-full place-items-center text-ink-muted">Loading board…</div>
        )}
      </main>

      <footer className="flex items-center justify-between px-8 py-3 border-t border-white/10 text-sm text-ink-dim">
        <span>
          {rotationPanels.length} panels · {(config?.rotation_seconds ?? 5)}s rotation
          {' · '}
          <span className="text-ink-muted">click to advance, shift-click back, ← → work too</span>
        </span>
        {/* The dots are buttons now. They were decorative, which made the
            only visible affordance on the page the one thing that could not
            be used. Note they map over rotationPanels, not panels: the
            adventure is a rotation slot, and using `panels` here meant the
            highlight was off by one whenever an adventure was active. */}
        <div className="flex items-center gap-1">
          {rotationPanels.map((p, i) => (
            <button
              key={p + i}
              type="button"
              aria-label={`Show ${p}`}
              aria-current={i === idx}
              onClick={() => goTo(i)}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${i === idx ? 'w-8 bg-accent' : 'w-4 bg-bg-elevated hover:bg-ink-dim'}`}
            />
          ))}
        </div>
      </footer>
    </div>
  );
}

// ---- Phase 5: Adventure panel ----
//
// v0.8.9: full-width Adventure panel. The previous composite
// (adventure + live feed sidebar in one grid) was split into
// two separate rotation slots. The Adventure panel now owns
// only the campaign headline + goal progress; the live feed
// runs as its own opt-in rotation panel ('⚡ Live Feed' in
// Public Board settings).
//
// Rendered when the team has an active adventure. No recap
// footer — the operator asked for the previous-adventure recap
// to be hidden while the current adventure is running.

function AdventureBoard({ adventure }: { adventure: NonNullable<BoardResponse['adventure']> }) {
  const active = adventure.active!;
  const dayLabel = dayCounter(active.starts_at, active.ends_at);
  const pct = active.predictability_pct;
  // Where the campaign is on its own clock, 0..1.
  //
  // Operator asked for this on THIS screen: "its the screen that you get when
  // you click Public Board". I built it on the Adventure admin page first and
  // they could not see it, which was the right answer to the wrong question.
  const elapsed = elapsedFraction(active.starts_at, active.ends_at);
  const elapsedPct = Math.round(elapsed * 100);
  // TIME LEFT is what the operator asked for; the percentage is what the
  // marker draws. Both, because the number answers the question and the
  // marker answers "am I ahead of it".
  const msLeft = new Date(active.ends_at).getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
  const timeLeft = msLeft <= 0 ? 'campaign over'
    : daysLeft >= 2 ? `${daysLeft} days left`
      : msLeft < 3600000 ? `${Math.max(1, Math.round(msLeft / 60000))} min left`
        : `${Math.max(1, Math.round(msLeft / 3600000))} hours left`;
  // Softer than a hard red: high = emerald, mid = amber, low = a calm
  // rose rather than alarm-red.
  const textClass = pct >= 80 ? 'text-emerald-300' : pct >= 50 ? 'text-amber-300' : 'text-blue-400';
  // v0.8.9: brand-aligned blend — gold → black, matching the
  // M-logo on the header. Same look for every pct tier; the
  // 4% number above the bar carries the status signal.
  const barClass = 'from-[var(--fantasy-gold)] to-black';

  return (
    <div className="h-full flex flex-col px-8 py-6 gap-4" style={{ fontSize: 'clamp(0.875rem, 0.75vw, 1.2rem)' }}>
      {/* Campaign headline + goals. Full width. Live feed no
          longer in this view — it's its own rotation slot. */}
      <div className="flex-1 bg-bg-elevated border border-white/10 rounded-lg p-6 flex flex-col min-h-0">
        <div className="flex items-baseline justify-between mb-2">
          <h1 className="text-2xl font-bold text-amber-300">
            {active.place ? `The ${active.place} of ${active.name}` : active.name}
          </h1>
          <span className="text-ink-muted">{dayLabel}</span>
        </div>
        <div className="flex items-baseline gap-3 mb-2">
          <span className={`text-4xl font-bold ${textClass}`}>{pct}%</span>
          <span className="text-ink-muted">campaign progress</span>
          <span className="ml-auto flex items-baseline gap-3 text-ink-muted">
            <span>
              {active.completed_count}/{active.committed_count} challenges cleared
            </span>
            {/* v0.8.9: side-quest tally. Tickets the team closed
                in the adventure window that weren't part of the
                original commitment — surface as unplanned wins
                alongside the strict campaign progress. Skipped
                when there are zero so the header doesn't get
                noisy on a tidy sprint. */}
            {active.side_quest_count !== undefined && active.side_quest_count > 0 && (
              <span className="text-amber-300/80" title="tickets closed in this adventure that weren't in the original commitment">
                + {active.side_quest_count} side quest{active.side_quest_count === 1 ? '' : 's'} completed
              </span>
            )}
          </span>
        </div>
        {/* progress bar — recessed track + raised glossed fill for depth */}
        <div className="relative rounded-full border border-white/10 bg-black/40 p-[3px] shadow-inner mb-4">
          <div className={`relative h-4 rounded-full bg-gradient-to-b ${barClass} shadow-[0_2px_6px_rgba(0,0,0,0.5)]`} style={{ width: `${Math.max(pct, 2)}%` }}>
            {/* gloss highlight on the top half of the bar */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-full bg-gradient-to-b from-white/35 to-transparent" />
          </div>
          {/* WHERE NOW IS.
              In the TRACK, not in the fill. The fill is a rounded pill inside
              a padded track, and a marker inside it would be clipped by its
              own rounded ends and lost against the gloss — which is exactly
              what happened on the Adventure admin page, where the marker went
              inside an 8px bar that clips.
              The track is 22px tall against the fill's 16, and does not clip,
              so the marker stands proud above and below the bar and reads as
              a mark rather than a smudge on the fill.
              Near-white, deliberately not any of the three state colours:
              green, amber, red all mean something about the PROGRESS, and
              this is the clock. */}
          {elapsed > 0 ? (
            <div
              className="pointer-events-none absolute inset-y-0 w-[3px] rounded-full bg-white/90 shadow-[0_0_6px_rgba(0,0,0,0.9)]"
              style={{ left: `${elapsedPct}%`, marginLeft: '-1.5px' }}
              title={`${elapsedPct}% of the campaign's time has gone`}
            />
          ) : null}
        </div>
        {/* The comparison stated in words, because the drawn version needs the
            reader to know what the tick means. */}
        <div className="mb-4 -mt-3 flex items-baseline gap-3 text-sm">
          <span className="font-semibold text-ink">{timeLeft}</span>
          {/* The comparison stated in words, because the drawn version needs
              the reader to know what the tick means.
              Hours under a day, minutes under an hour: `ceil` to days means
              the WHOLE last day reads "1 days left", including its final
              minute, and that is exactly when the number matters. */}
          <span className={pct >= elapsedPct ? 'text-accent-success/80' : 'text-amber-300/90'}>
            {elapsedPct}% of the clock gone{pct >= elapsedPct ? ' — ahead of it' : ' — behind it'}
          </span>
        </div>
        {/* goals as individual boxes, matching the boss-box look */}
        {/* v0.8.9: goals as horizontal banner strips. Each goal
            is its own row (not a wrapped mini-box) with a
            notched-flag left edge, a thin progress strip on
            the bottom (visual fill ratio = g.progress / g.target
            clamped to [0,1]), and the goal name + sub-line.
            Hit goals switch the progress strip to gold to read
            as a victory ribbon. */}
        {active.goals.length > 0 ? (
          <div className="space-y-2">
            {/* GOALS HEADING: labels the strips below as this
                adventure's goals. Without it the rows read as
                generic progress bars to anyone who didn't author
                them. The hairline rule runs to the right so the
                heading reads as a section divider. Lives inside
                the space-y container so it only renders when
                there is at least one goal. */}
            <div className="flex items-center gap-3 pt-1 text-xs uppercase tracking-[0.18em] text-amber-200/70">
              <span>⚔ Goals</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
            {active.goals.map((g) => {
              const pct = g.kind === 'tickets' && g.target > 0
                ? Math.min(1, Math.max(0, (g.progress ?? 0) / g.target))
                : 0;
              return (
                <div
                  key={g.id}
                  className={`px-4 py-2 rounded ${g.hit_now ? 'bg-[#fbbf2433] border-l-2 border-amber-400/70 shadow-[0_0_18px_rgba(251,191,36,0.22)]' : 'bg-black/30 border-y border-r border-white/10'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className={`truncate font-semibold ${g.hit_now ? 'text-gold' : 'text-ink'}`}>
                      {g.name}
                    </span>
                    {g.kind === 'tickets' && (
                      <span className="text-sm text-ink-muted tabular-nums whitespace-nowrap">
                        {g.progress ?? 0}/{g.target} challenges completed
                      </span>
                    )}
                  </div>
                  {g.kind === 'tickets' && g.users && g.users.length > 0 && (
                    <div className="mt-1">
                      <PersonChips users={g.users} className="mt-0" />
                    </div>
                  )}
                  <div className="mt-2 rounded-full border border-white/10 bg-black/40 p-[2px] shadow-inner">
                    <div className={`relative h-2 rounded-full overflow-hidden transition-all shadow-[0_1px_3px_rgba(0,0,0,0.5)] ${g.hit_now ? 'bg-gold' : 'bg-gradient-to-b from-zinc-300 to-zinc-600'}`} style={{ width: `${(pct * 100).toFixed(1)}%` }}>
                      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-full bg-gradient-to-b from-white/35 to-transparent" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-ink-muted mt-4">No goals authored yet.</p>
        )}
      </div>
    </div>
  );
}

// Small name chips for "who's on this goal / boss fight". Renders
// nothing when there are no attributed members. Sized between intrusive
// and unreadable — visible on the wall TV but out of the way of the name.
function PersonChips({ users, className }: { users?: Array<{ user_id: string; handle: string; name: string | null; color?: string | null }>; className?: string }) {
  if (!users || users.length === 0) return null;
  return (
    <div className={`mt-2 flex flex-wrap gap-1.5 ${className ?? ''}`}>
      {users.map((u) => (
        <span
          key={u.user_id}
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-gradient-to-b from-white/10 to-white/5 px-2.5 py-0.5 text-xs text-ink ring-1 ring-inset ring-amber-300/10 shadow-[0_0_8px_rgba(251,191,36,0.18)]"
        >
          {/* The player's personal colour as a "gem" dot — the party
              badge look. Falls back to slate when unset. */}
          <span
            className="h-2 w-2 rounded-full ring-1 ring-black/40"
            style={{ backgroundColor: u.color ?? '#64748b' }}
          />
          {u.name ?? u.handle}
        </span>
      ))}
    </div>
  );
}

// Day counter: 'Day 9 of 14' based on the adventure window.
function dayCounter(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  const total = Math.max(1, Math.round((end - start) / 86400000));
  const elapsed = Math.max(0, Math.min(total, Math.round((Date.now() - start) / 86400000)));
  return `Day ${elapsed + 1} of ${total}`;
}

function Panel({ name, data, adventure }: { name: string; data: PanelData; adventure: NonNullable<BoardResponse['adventure']> | null }) {
  switch (name) {
    case 'adventure':
      // v0.8.9: the adventure three-panel layout is now a slot
      // in the rotation rather than the always-on screen when an
      // adventure is active. Operators that want the live
      // predictability headline visible at all times can disable
      // every other panel in Public Board settings; the rotation
      // collapses to a single static slot.
      if (!adventure) {
        return <div className="grid h-full place-items-center text-ink-muted">No active adventure.</div>;
      }
      return <AdventureBoard adventure={adventure} />;
    case 'on_fire': return <OnFirePanel data={data.on_fire ?? []} />;
    case 'just_shipped': return <JustShippedPanel data={data.just_shipped ?? []} />;
    case 'kudos_stream': return <KudosStreamPanel data={data.kudos_stream ?? []} />;
    case 'quest_of_the_day': return <QuestOfDayPanel data={data.quest_of_the_day ?? null} />;
    case 'class_spotlight': return <ClassSpotlightPanel data={data.class_spotlight ?? {}} />;
    case 'todays_standouts': return <TodaysStandoutsPanel data={data.todays_standouts ?? []} />;
    case 'competency_heatmap': return <HeatmapPanel data={data.competency_heatmap ?? { competencies: [], avgs: [] }} />;
    case 'boss_fights':
      // Same carousel as /boss-fights. The admin can opt in to
      // this panel via Admin → Public Board settings; off by
      // default so the existing rotation behaviour is unchanged.
      return (
        <PanelShell
          title="👹 Boss Fights"
          subtitle="Active boss fights. The bar fills as closed tickets deal damage; HP is fixed at the start of the fight."
        >
          {adventure?.bosses && adventure.bosses.length > 0 ? (
            <Carousel className="w-full pb-1">
              {adventure.bosses.map((b) => (
                <BossCard
                  key={b.id}
                  boss={{
                    id: b.id,
                    name: b.name,
                    hp: b.hp ?? 0,
                    damage_dealt: b.damage_dealt,
                    status: (b.status === 'archived' ? 'survived' : b.status) as 'active' | 'vanquished' | 'survived',
                    ends_at: b.ends_at,
                    tickets_total: b.tickets_total,
                    tickets_closed: b.tickets_closed,
                    users: (b.users ?? []).map((u) => ({
                      user_id: u.user_id,
                      handle: u.handle,
                      name: u.name,
                      damage: u.damage ?? 0,
                      color: u.color ?? null,
                    })),
                  }}
                  sprite={spriteFor(b.name)}
                  variant="full"
                  size="lg"
                  engine="3d"
                />
              ))}
            </Carousel>
          ) : (
            <div className="grid h-full place-items-center text-ink-muted">No active boss fights.</div>
          )}
        </PanelShell>
      );
    case 'live_feed':
      // The live feed panel is self-contained: it has its
      // own useQuery (2s refetch, independent of the
      // parent /api/public-board query), its own visible-
      // list state, and its own animation orchestration.
      // The PanelShell gives it the same title / subtitle
      // chrome as the other panels so the wall TV looks
      // consistent across rotations.
      return (
        <PanelShell
          title="⚡ Live Feed"
          subtitle="The 20 most recent events from team members, newest first. New rows fade in at the top, the oldest slides off the bottom. Each row shows the event identifier, the scoring rule description (when the rule has one), and the XP + coin total earned."
        >
          <LiveFeedPanel />
        </PanelShell>
      );
    default: return <div className="grid h-full place-items-center text-ink-muted">Unknown panel: {name}</div>;
  }
}

function displayName(u: BoardUser): string { return u.name ?? '@' + u.handle; }

function PanelShell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto flex h-full max-w-6xl flex-col px-8 py-4">
      {/*
       * Header: ONE row, title left, subtitle right.
       *
       * It used to be two stacked blocks - a text-3xl title with mb-6 and a
       * paragraph beneath - which together took a large slice of a panel whose
       * real job is the list under it. The list is what people read; the
       * heading only has to say where they are.
       *
       * The subtitle still exists for the admin who walks up to the wall to
       * understand a rule, but it no longer costs height: it sits inline,
       * truncated to one line, with the full text on the title attribute and
       * the whole thing still expandable with T.
       */}
      <div className="mb-3 flex items-baseline gap-4">
        <h1 className="shrink-0 text-xl font-bold uppercase tracking-wide text-accent">{title}</h1>
        {/*
         * Subtitle: a "what is this" hint rendered as small, low-contrast
         * text directly under the panel title. The intent is that the
         * rotating wall TV (5s per panel, 1m+ viewing distance) does NOT
         * compete with this text for attention — the title is the focal
         * point. An admin walking up to the wall to inspect the board
         * can read the subtitle and immediately understand the rule
         * behind the ranking ("top 5 by XP awarded today, filtered to
         * team members"). Press T to toggle full opacity (a11y /
         * keyboard users, or anyone who actually wants the hint
         * bigger). Default state matches the wall-TV aesthetic.
         */}
        {subtitle ? <PanelSubtitle text={subtitle} /> : null}
      </div>
      {/* v0.8.9: min-h-0 + overflow-hidden so this flex-1
          region is bounded to the available panel height
          instead of growing with its content. Without it the
          flex item's automatic minimum size (= content) wins,
          the region overflows PanelShell, and any child that
          wants to scroll (live feed) never gets a definite
          height to scroll inside. */}
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

// Subtitle component: small, dim, optional "enlarged" mode toggled by
// the T key. The enlarge mode is mostly for accessibility — users
// with low vision who DO want to read the hint, or for a wall monitor
// that has been zoomed in via the browser. The default dim state is
// the "wall TV at 1m+" use case; enlarged state is "up close reading
// intentionally". The hint is a plain <p> with a `title` attribute
// holding the same text, so a screen reader gets it regardless of
// the visual state.
function PanelSubtitle({ text }: { text: string }) {
  const [enlarged, setEnlarged] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Only react to plain T (no modifiers, not in an input)
      if (e.key === 't' || e.key === 'T') {
        const target = e.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
        if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
        e.preventDefault();
        setEnlarged((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <p
      title={text}
      className={enlarged
        ? 'min-w-0 flex-1 text-sm text-ink-muted'
        : 'min-w-0 flex-1 truncate text-xs text-ink-dim/70'}
    >
      {text}
    </p>
  );
}

function OnFirePanel({ data }: { data: { handle: string; name: string | null; streak: number }[] }) {
  return (
    <PanelShell title="🔥 On Fire" subtitle="Top 5 team members by current streak (consecutive days with at least one XP-earning event).">
      <ul className="space-y-2">
        {data.map((u, i) => (
          <li key={u.handle} className="flex items-center justify-between rounded-lg bg-bg-panel px-4 py-2">
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold text-ink-dim">#{i + 1}</span>
              <span className="text-xl font-semibold">{displayName(u)}</span>
            </div>
            <span className="text-2xl font-bold text-accent-warning">{u.streak} 🔥</span>
          </li>
        ))}
        {data.length === 0 ? <Empty /> : null}
      </ul>
    </PanelShell>
  );
}

function JustShippedPanel({ data }: { data: { type: string; when: string; who: BoardUser; title: string }[] }) {
  return (
    <PanelShell title="🚀 Just Shipped" subtitle="The 8 most recent merged PRs and completed quests from team members, newest first.">
      <ul className="space-y-1.5">
        {data.map((r, i) => (
          <li key={i} className="flex items-center justify-between rounded-lg bg-bg-panel px-4 py-2">
            <div>
              <span className="text-lg font-semibold">{displayName(r.who)}</span>
              <span className="ml-3 text-base text-ink-muted">{r.title}</span>
            </div>
            <span className="text-base text-ink-dim">{r.type === 'quest.completed' ? '🎯 quest' : '✓ merged'}</span>
          </li>
        ))}
        {data.length === 0 ? <Empty /> : null}
      </ul>
    </PanelShell>
  );
}

function KudosStreamPanel({ data }: { data: { to: BoardUser; from: string; note: string | null }[] }) {
  return (
    <PanelShell title="💛 Kudos Stream" subtitle="The 10 most recent kudos given on this team, newest first.">
      <ul className="space-y-1.5">
        {data.map((k, i) => (
          <li key={i} className="rounded-lg bg-bg-panel px-4 py-2">
            <span className="text-lg font-semibold">{k.from}</span>
            <span className="text-base text-ink-muted"> → </span>
            <span className="text-lg font-semibold text-accent">{displayName(k.to)}</span>
            {k.note ? <p className="mt-1 text-base text-ink-muted">“{k.note}”</p> : null}
          </li>
        ))}
        {data.length === 0 ? <Empty /> : null}
      </ul>
    </PanelShell>
  );
}

function QuestOfDayPanel({ data }: { data: { id: string; title: string; description: string; splits: Array<{ competency_id: string; xp: number; coins?: number }> } | null }) {
  // M5.15: render total XP + total coins + per-split
  // breakdown. The panel resolves competency names via
  // the competencies query (already loaded by the
  // heatmap panel); the splits show as "+5 Quality, +3
  // Communication" rather than a flat total. Empty
  // splits (a ceremonial / recognition quest) shows
  // "No XP awarded" instead of a misleading "+0 XP".
  const total = data?.splits.reduce((s, x) => s + x.xp, 0) ?? 0;
  const totalCoins = data?.splits.reduce((s, x) => s + (x.coins ?? 0), 0) ?? 0;
  return (
    <PanelShell title="🗺️ Quest of the Day" subtitle="One rotating quest highlighted from the team's open quest pool, chosen server-side (highest XP that isn't completed and isn't expired).">
      {data ? (
        <div className="rounded-lg bg-bg-panel p-8">
          <h2 className="text-4xl font-bold">{data.title}</h2>
          {data.description ? <p className="mt-3 text-2xl text-ink-muted">{data.description}</p> : null}
          <div className="mt-6 flex items-baseline gap-4 text-3xl font-bold">
            <span className={total > 0 ? 'text-accent-success' : 'text-ink-dim'}>
              {total > 0 ? `+${total} XP` : 'No XP awarded'}
            </span>
            {totalCoins > 0 ? (
              <span className="text-accent-warning">+{totalCoins} 🪙</span>
            ) : null}
          </div>
          {data.splits.length > 0 ? (
            <ul className="mt-3 space-y-1 text-xl text-ink-muted">
              {data.splits.map((s, i) => (
                <li key={i}>
                  +{s.xp} {s.competency_id}
                  {(s.coins ?? 0) > 0 ? <> · +{s.coins} 🪙</> : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : <Empty text="No open quests right now." />}
    </PanelShell>
  );
}

function ClassSpotlightPanel({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).filter(([k]) => k !== 'none');
  return (
    <PanelShell title="⚔️ Class Spotlight" subtitle="Team distribution across the four Forbury classes (crafter / guardian / explorer / diplomat). Counted from the most recent class choice or auto-class per player.">
      <ul className="space-y-2">
        {entries.map(([cls, n]) => (
          <li key={cls} className="flex items-center justify-between rounded-lg bg-bg-panel px-4 py-2">
            <span className="text-2xl">{CLASS_ICONS[cls] ?? '·'} <span className="text-xl font-semibold capitalize">{cls}</span></span>
            <span className="text-2xl font-bold text-accent">{n}</span>
          </li>
        ))}
        {entries.length === 0 ? <Empty /> : null}
      </ul>
    </PanelShell>
  );
}

function TodaysStandoutsPanel({ data }: { data: { handle: string; name: string | null; xp: number }[] }) {
  return (
    <PanelShell title="⭐ Recent Standouts" subtitle="Top 5 team members by total XP earned in the last 24 hours (rolling window). Filtered to current team members and excludes ignored / service accounts. Events from Jira users who aren't in Mirabili yet are scored for audit but produce no award.">
      <ul className="space-y-2">
        {data.map((u, i) => (
          <li key={u.handle} className="flex items-center justify-between rounded-lg bg-bg-panel px-4 py-2">
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold text-ink-dim">#{i + 1}</span>
              <span className="text-xl font-semibold">{u.name ?? '@' + u.handle}</span>
            </div>
            <span className="text-xl font-bold text-accent-success">+{u.xp}</span>
          </li>
        ))}
        {data.length === 0 ? <Empty text="No XP awarded in the last 24 hours." /> : null}
      </ul>
    </PanelShell>
  );
}

function HeatmapPanel({ data }: { data: { competencies: { id: string; name: string; dimension: string }[]; avgs: { competency_id: string; avg: number }[] } }) {
  // v0.6.2: defensive guards. The data prop is typed, but
  // the public board can be rendered with stale data from
  // a partial response. `data.avgs.map` was unsafe — same
  // anti-pattern as the previous fix rounds. The local
  // `?? []` defaults make the chart render an empty state
  // instead of crashing if the prop is partial.
  const avgs = data?.avgs ?? [];
  const competencies = data?.competencies ?? [];
  const avgMap = new Map(avgs.map((a) => [a.competency_id, a.avg]));
  return (
    <PanelShell title="🧊 Competency Heatmap" subtitle="Average per-competency level across the team (mean of each member's current level in that competency). Cell brightness = average level. Empty cells mean no team member has progress in that competency yet.">
      <ul className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {competencies.map((c) => {
          const avg = avgMap.get(c.id) ?? 0;
          const opacity = avg <= 0 ? 0 : Math.min(1, avg * 0.2);
          return (
            <li key={c.id} className="rounded-lg p-4" style={{ backgroundColor: avg > 0 ? `rgba(99,102,241,${opacity})` : 'var(--bg-panel)' }}>
              <p className="text-xl font-semibold">{c.name}</p>
              <p className="text-lg text-ink-muted">avg L{avg.toFixed(1)}</p>
            </li>
          );
        })}
      </ul>
    </PanelShell>
  );
}

function Empty({ text = 'Nothing to show yet.' }: { text?: string }) {
  return <p className="text-lg text-ink-dim">{text}</p>;
}

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <span className="tabular-nums">{now.toLocaleTimeString()}</span>;
}

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches !== reduce) setReduce(mq.matches);
  }
  return reduce;
}

// Look up the display name for a team_id. Used in the board header
// to confirm to operators which team the wall TV is currently
// showing. The board response only carries team_id; we fetch the
// team name lazily (it'll be the same id as long as the wall TV
// is open, so we cache by id).
function useTeamName(teamId: string | undefined, fallbackName?: string | null): string | null {
  // Prefer the name from the board response itself (the API
  // includes it directly so the public board — which is
  // anonymous — doesn't need a separate auth call to render it).
  // The /api/teams lookup below is a fallback for legacy responses
  // that don't carry team_name.
  const [name, setName] = useState<string | null>(fallbackName ?? null);
  useEffect(() => { if (fallbackName !== undefined) setName(fallbackName); }, [fallbackName]);
  useEffect(() => {
    if (!teamId) { setName(null); return; }
    if (fallbackName) return; // Already have it from the response.
    let cancelled = false;
    api<{ teams: Array<{ id: string; name: string }> }>('/api/teams')
      .then((d) => {
        if (cancelled) return;
        const t = d.teams.find((x) => x.id === teamId);
        setName(t?.name ?? null);
      })
      .catch(() => { if (!cancelled) setName(null); });
    return () => { cancelled = true; };
  }, [teamId, fallbackName]);
  return name;
}